import { IonIcon } from "@ionic/react";
import { downloadSharp, printSharp, shareSocialSharp } from "ionicons/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { useSearchParams } from "react-router-dom";
import ProductObj from "../../OBJRep/Product.js";
import ResourceSearchResult from "../../OBJRep/ResourceSearchResult.js";
import {
  calcFontSizeByNameLength,
  mapProductsWithFontSize,
  moveProductIntoSlot,
} from "../../services/productFontSize.js";
import {
  convertPriceStyleToDataURL,
  convertProductsImagesToDataURL,
  isCartazFormat,
} from "../../services/Utils.js";
import Button from "../sharable/ActionButton.jsx";
import Loading from "../sharable/Loading.jsx";
import Logo from "../sharable/Logo.jsx";
import {
  CANVAS_TARGET_CLASSNAME,
  CANVAS_VIWER_TARGET_CLASSNAME,
  CANVAS_WRAPPER_PRINT_TARGET_CLASSNAME,
  CESTA_BASICA_KIT,
  FEED_INSTAGRAM,
  FEED_INSTAGRAM_PORTRAIT,
  MAX_PRODUCTS,
  MAX_PRODUCTS_BY_FORMAT,
  PANFLETO,
  STORIES,
} from "../WorkSpace/CanvasViewer/EncarteConstants.js";
import CanvasViewer from "../WorkSpace/CanvasViewer/SocialMediaCanvasViewer.jsx";
import { removeProductGridStyles } from "../WorkSpace/ToolBarMenuViews/GridMenuView/GridMenuView.jsx";
import Toolbar, {
  PRODUCTS_VIEW,
  STYLES_VIEW,
} from "../WorkSpace/ToolBarMenuViews/Toolbar.jsx";
import WorkspaceContext from "../WorkSpace/WorkspaceContext.js";

import Help from "../sharable/Help.jsx";
import Notification from "../sharable/Notification.jsx";
import PremiumTag from "../sharable/PremiumTag.jsx";
import { processProductsWithThemeStyles } from "../WorkSpace/Editor/processesProductsWithThemeStyles.js";
import {
  AUTO_GRID_POSITIONS_FEED,
  AUTO_GRID_POSITIONS_FEED_PORTRAIT,
  AUTO_GRID_POSITIONS_PANFLETO,
  AUTO_GRID_POSITIONS_STORIES,
  Grid,
} from "../WorkSpace/ToolBarMenuViews/GridMenuView/Grids.js";
import useFetchTheme from "../WorkSpace/ToolBarMenuViews/ThemesMenuView.jsx/hooks/useFetchTheme.js";
import buildCaptionText, {
  DEFAULT_CAPTION_TOGGLES,
} from "../WorkSpace/ToolBarMenuViews/CaptionMenuView/buildCaptionText.js";
import useCanvasDownload from "./hooks/useCanvasDownload.js";
import useCanvasPrint from "./hooks/useCanvasPrint.js";
import useCanvasShare from "./hooks/useCanvasShare.js";
import {
  Container,
  EncarteNameBox,
  ExportButtonWrapper,
  ExportOtpions,
  Header,
  Layout,
} from "./Workspace.styles.js";
const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

export default function WorkSpace() {
  const { token, user, isAccountPremium, accountStatus } = useOutletContext();
  const [encarte, setEncarte] = useState();
  const [canvasScale, setCanvasScale] = useState(0.5);
  const [params] = useSearchParams();
  const [userBrand, setUserBrand] = useState();
  const [maxProducts, setMaxProducts] = useState(MAX_PRODUCTS);
  const [products, setProducts] = useState([]);
  const productRefs = useRef({});
  const [encarteName, setEncarteName] = useState("");
  const [existUpdates, setExistUpdates] = useState(false);
  const [isEncarteDownloading, setIsEncarteDownloading] = useState(false);
  const [isEncarteUpdating, setIsEncarteUpdating] = useState(false);
  const [encarteThemesSearchResult, setEncarteThemesSearchResult] = useState(
    ResourceSearchResult(),
  );
  const [
    isDownloadingEncarteProductImages,
    setIsDownloadingEncarteProductImages,
  ] = useState(false);
  const menuRef = useRef();
  const [priceThemesSearchResult, setPriceThemesSearchResult] = useState(
    ResourceSearchResult(),
  );
  const [userProductsPage, setUserProductsPage] = useState();
  const [grid, setGrid] = useState(undefined);
  const [currentMenuView, setCurrentMenuView] = useState(PRODUCTS_VIEW);

  const [canvasSelectedProductIndex, setCanvasSelectedProductIndex] =
    useState(null);

  const [captionToggles, setCaptionToggles] = useState(DEFAULT_CAPTION_TOGGLES);

  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();

  const { model, setModel } = useFetchTheme({
    token,
    encarte,
    setIsEncarteUpdating,
  });
  const [styles, setStyles] = useState();
  const { printCanvas } = useCanvasPrint({
    canvasWrapperPrintTargetClassName: CANVAS_WRAPPER_PRINT_TARGET_CLASSNAME,
  });

  const caption = useMemo(
    () =>
      buildCaptionText({
        modelName: model?.name,
        userBrand,
        products,
        startDate: encarte?.startDate,
        endDate: encarte?.endDate,
        toggles: captionToggles,
      }),
    [model, userBrand, products, encarte, captionToggles],
  );

  const { downloadCanvas } = useCanvasDownload({
    encarteName,
    canvasScale,
    setCanvasScale,
    setCanvasSelectedProductIndex,
    canvasTargetClassName: CANVAS_TARGET_CLASSNAME,
    format: styles?.format,
    products,
    canDownload: Boolean(user?.accountVerified) || !accountStatus?.onTrial,
    onBlockedDownload: () => {
      setNotification({
        message: "Verique seu email para poder baixar seu encarte.",
        type: "info",
        autoHide: true,
        duration: 5000,
      });
    },
  });

  const { shareCanvas } = useCanvasShare({
    encarteName,
    shareText: caption,
    canvasScale,
    setCanvasScale,
    setCanvasSelectedProductIndex,
    canvasTargetClassName: CANVAS_TARGET_CLASSNAME,
    canShare:
      Boolean(accountStatus?.isAccountActive) || !accountStatus?.onTrial,
    onBlockedShare: () => {
      setNotification({
        message: "Verique seu email para poder baixar seu encarte.",
        type: "info",
        autoHide: true,
        duration: 5000,
      });
    },
  });

  const onStylesChange = (style, value) => {
    if (style === "format" && value !== styles.format) {
      const max = MAX_PRODUCTS_BY_FORMAT[value] || 12;
      let updatedProducts = [...products];
      if (updatedProducts.length > max) {
        updatedProducts = updatedProducts.slice(0, max);
      }
      const assignedPositions = calcGridAndAssignPositions(
        updatedProducts,
        value,
      );
      const processedProducts = processProductsWithThemeStyles(
        assignedPositions,
        model,
        setIsEncarteUpdating,
        styles?.format,
      );
      setProducts(processedProducts);
    }
    const stylesCopy = { ...styles };
    stylesCopy[style] = value;
    setStyles(stylesCopy);
    setExistUpdates(true);
  };

  const onGridChange = (obj) => {
    setIsEncarteUpdating(true);
    if (!obj?.grid) {
      const productsWithoutGridStyles = removeProductGridStyles(products);
      const processedProducts = processProductsWithThemeStyles(
        productsWithoutGridStyles,
        model,
        setIsEncarteUpdating,
        styles?.format,
      );
      setProducts(processedProducts);
      setGrid(null);
    } else {
      setProducts(obj?.products);
      const mappedProducts = mapProductsWithFontSize(
        obj?.products,
        obj?.products.styles?.fontSize || 32,
      );
      const processedProducts = processProductsWithThemeStyles(
        mappedProducts,
        model,
        setIsEncarteUpdating,
        styles?.format,
      );
      setProducts(processedProducts);
      setGrid(obj?.grid);
    }
    setExistUpdates(true);
    setIsEncarteUpdating(false);
  };

  const onProductsChange = (arr) => {
    setIsEncarteUpdating(true);
    setExistUpdates(true);
    if (arr.length != products.length) {
      const assignedPositions = calcGridAndAssignPositions(arr, styles.format);

      const processedProducts = processProductsWithThemeStyles(
        assignedPositions,
        model,
        setIsEncarteUpdating,
        styles?.format,
      );
      setProducts(processedProducts);
      setIsEncarteUpdating(false);
      return;
    }
    setProducts(arr);
    setIsEncarteUpdating(false);
  };

  const onCreateNewProduct = useCallback(() => {
    const newProducts = [...products];
    const pid = newProducts.length;
    const product = ProductObj(pid);
    newProducts.push(product);
    const processedProducts = processProductsWithThemeStyles(
      newProducts,
      model,
      setIsEncarteUpdating,
      styles?.format,
    );
    onProductsChange(processedProducts);
    setTimeout(() => {
      if (productRefs.current[pid]) {
        scrollIntoProduct(pid);
      }
    }, 100);
  }, [products, model]);

  const onCanvasProductSelect = useCallback(
    (pid, action) => {
      setCanvasSelectedProductIndex(pid);
      if (action === "edit") {
        setCurrentMenuView(PRODUCTS_VIEW);
        setTimeout(() => {
          scrollIntoProduct(pid);
        }, 100);
      } else if (action === "style") {
        scrollIntoMenu(STYLES_VIEW);
      }
    },
    [products],
  );

  const scrollIntoProduct = (pid) => {
    if (productRefs.current[pid]) {
      productRefs.current[pid].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollIntoMenu = (menu) => {
    setCurrentMenuView(menu || PRODUCTS_VIEW);
    setTimeout(() => {
      if (menuRef.current) {
        menuRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const onEncarteChange = (obj) => {
    setIsEncarteUpdating(true);
    setExistUpdates(true);
    setEncarte(obj);
    setIsEncarteUpdating(false);
  };
  const onEncarteThemeChange = (obj) => {
    setIsEncarteUpdating(true);
    setExistUpdates(true);
    setModel(obj);
    setIsEncarteUpdating(false);
  };

  const onEncarteNameChange = (obj) => {
    setExistUpdates(true);
    setEncarteName(obj);
  };

  const onUserBrandChange = (obj) => {
    setIsEncarteUpdating(true);
    setExistUpdates(true);
    setUserBrand(obj);
    setIsEncarteUpdating(false);
  };

  const onEncarteThemesSearchResultChange = useCallback(
    (encarteThemesSearchResult) => {
      setEncarteThemesSearchResult(encarteThemesSearchResult);
    },
    [],
  );

  const onPriceThemesSearchResultChange = useCallback(
    (priceThemesSearchResult) => {
      setPriceThemesSearchResult(priceThemesSearchResult);
    },
    [],
  );

  const onUserProductsPageChange = useCallback((object) => {
    setUserProductsPage(object);
  }, userProductsPage);
  const loadEncarte = useEffect(() => {
    const perform = async () => {
      try {
        if (!user) return;
        const encarteId = Number(params.get("encarteId"));
        const res = await fetch(`${BASE_URL}encarte-collection/${encarteId}`, {
          headers: { Authorization: token },
        });
        const element = await res.json();
        /*CONVERTS BRAND LOGO TO DATA URL TO BE POSSIBLE TO DOWNLOAD */

        if (element.styles.rows && element.styles.columns) {
          setGrid(Grid(element.styles.columns, element.styles.rows));
          setProducts(element.products);
        } else {
          setProducts(
            calcGridAndAssignPositions(element.products, element.styles.format),
          );
        }
        /*CONVERTES PRODUCTS IMAGES TO DATA URL TO BE POSSIBLE TO DOWNLOAD */
        setIsDownloadingEncarteProductImages(true);
        progressivelyLoadProductsImgs(
          element.products,
          token,
          setProducts,
          setIsDownloadingEncarteProductImages,
        );
        if (element.startDate) element.startDate = new Date(element.startDate);
        if (element.endDate) element.endDate = new Date(element.endDate);
        setUserBrand(user.brand);
        setEncarte(element);
        setStyles(element.styles);
        setEncarteName(element.name);
      } catch (err) {
        console.error(err);
        navigate("/encartes");
      }
    };
    perform();
  }, [user]);

  useEffect(() => {
    if (!model || !products?.length) return;
    const processedProducts = processProductsWithThemeStyles(
      products,
      model,
      setIsEncarteUpdating,
      styles?.format,
    );
    setProducts(processedProducts);
  }, [model]);

  const watchesCanvasOverflow = useEffect(() => {
    if (!model || !styles) return;
    const canvas = document.querySelector(`.${CANVAS_TARGET_CLASSNAME}`);
    const canvasWrapper = document.querySelector(
      `.${CANVAS_VIWER_TARGET_CLASSNAME}`,
    );
    if (!canvas || canvasScale === 3) return;
    const isOverflowing =
      canvas.offsetWidth > canvasWrapper.offsetWidth ||
      canvas.offsetHeight > canvasWrapper.offsetHeight;
    if (isOverflowing) {
      const s = canvasScale - 0.05;
      setCanvasScale(s);
    }
  }, [canvasScale, styles, model]);

  const watchEncarteChanges = useEffect(() => {
    const timer = setTimeout(() => {
      saveEncarte();
    }, 15000);
    return () => {
      clearTimeout(timer);
    };
  }, [products, encarteName, styles, model]);
  /* This effect ensures that when the format changes, the products array is updated to match the new format.
     If the format is "CARTAZ_PEQUENO", the products array is limited to 6 items.
      If the format is "CARTAZ_MEDIO", it is limited to 4 items.
      If the format is "CARTAZ_GRANDE", it is limited to 1 item.
      If the products array has fewer items than required, it fills the remaining slots with new Product objects.
     */
  useEffect(() => {
    if (!isCartazFormat(styles?.format)) return;
    let requiredCount = MAX_PRODUCTS_BY_FORMAT[styles?.format] || MAX_PRODUCTS;
    if (!products) return;
    let updatedProducts = [...products];
    if (products.length >= requiredCount) {
      updatedProducts = products.slice(0, requiredCount);
    } else {
      for (let i = products.length; i < requiredCount; i++) {
        updatedProducts.push(ProductObj(i));
      }
    }
    if (
      updatedProducts.length !== products.length ||
      updatedProducts.some((p, idx) => p.id !== products[idx]?.id)
    ) {
      onProductsChange(updatedProducts);
    }

    onProductsChange(updatedProducts);
  }, [styles?.format]);

  useEffect(() => {
    if (!styles?.format) return;
    const max = MAX_PRODUCTS_BY_FORMAT[styles.format] || 12;
    setMaxProducts(max);
  }, [styles?.format]);

  function calcGridAndAssignPositions(products, format) {
    let autoGridPositions = null;
    switch (format) {
      case FEED_INSTAGRAM:
        autoGridPositions = AUTO_GRID_POSITIONS_FEED;
        break;
      case FEED_INSTAGRAM_PORTRAIT:
        autoGridPositions = AUTO_GRID_POSITIONS_FEED_PORTRAIT;
        break;
      case PANFLETO:
      case CESTA_BASICA_KIT:
        autoGridPositions = AUTO_GRID_POSITIONS_PANFLETO;
        break;
      case STORIES:
        autoGridPositions = AUTO_GRID_POSITIONS_STORIES;
        break;
      default:
        return products;
    }
    return products.map((product, idx) => {
      const posStyles = autoGridPositions[products.length];
      const grid = posStyles.grid;
      const autoGridStyles = posStyles.styles[idx];
      setGrid(grid || {});
      const calculatedFontSize = calcFontSizeByNameLength(
        product.name,
        autoGridStyles.fontSize,
      );
      return {
        ...product,
        styles: {
          ...product.styles,
          gridColumn: autoGridStyles.column,
          gridRow: autoGridStyles.row,
          productLayout: autoGridStyles.layout,
          fontSize: calculatedFontSize,
        },
      };
    });
  }

  const saveEncarte = async () => {
    if (!existUpdates) return;
    //TODO:remove this function
    const arr = extractProdInfo(products);
    const en = { ...encarte };
    en.products = arr;
    en.modelUrl = model.url;
    en.styles = styles;
    if (grid) {
      en.styles.columns = grid.columns;
      en.styles.rows = grid.rows;
    } else {
      en.styles.columns = "";
      en.styles.rows = "";
    }

    en.name = encarteName;
    const encarteId = params.get("encarteId");
    const url = `${BASE_URL}encarte-collection/${encarteId}`;
    const res = await fetch(url, {
      method: "PUT",
      body: JSON.stringify(en),
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });
    if (res.ok) {
      setNotification({
        message: "Alterações salvas!",
        type: "success",
        autoHide: true,
        duration: 3000,
      });
      setExistUpdates(false);
    } else
      console.error("Erro ao salvar o encarte: ", res.statusText, res.status);
  };

  /**
   * Swaps the content of two products; each canvas slot keeps its own styles (and the selected grid).
   * @param {*} product1 index of the first product to swap
   * @param {*} product2 index of the second product to swap
   */
  const swapProductPositions = (product1, product2) => {
    setIsEncarteUpdating(true);
    const pArrCopy = [...products];
    const slot1 = pArrCopy[product1];
    const slot2 = pArrCopy[product2];
    pArrCopy[product1] = moveProductIntoSlot(slot2, slot1);
    pArrCopy[product2] = moveProductIntoSlot(slot1, slot2);
    setProducts(pArrCopy);
    setExistUpdates(true);
    setIsEncarteUpdating(false);
  };
  const workspaceContextValues = {
    maxProducts,
    onEncarteChange,
    encarte,
    onEncarteThemeChange,
    userBrand,
    onUserBrandChange,
    onProductsChange,
    products,
    onStylesChange,
    styles,
    model,
    userHasAccessToLoadedModel: isAccountPremium || !model?.isPremiumResource,
    canvasScale,
    onGridChange,
    grid,
    userProductsPage,
    onUserProductsPageChange,
    encarteThemesSearchResult,
    onEncarteThemesSearchResultChange,
    priceThemesSearchResult,
    onPriceThemesSearchResultChange,
    onCreateNewProduct,
    productRefs,
    onCanvasProductSelect,
    canvasSelectedProductIndex,
    currentMenuView,
    setCurrentMenuView,
    menuRef,
    scrollIntoMenu,
    swapProductPositions,
    isEncarteUpdating,
    isDownloadingEncarteProductImages,
    setIsEncarteUpdating,
    caption,
    captionToggles,
    onCaptionTogglesChange: setCaptionToggles,
  };

  return (
    <WorkspaceContext.Provider value={workspaceContextValues}>
      <Container>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            autoHide={notification.autoHide}
            duration={notification.duration}
            onClose={() => setNotification(null)}
          />
        )}
        <Header>
          <Logo size={"6"} showGoBackButton={true} path={"/encartes"} />

          <div className="flex min-gap">
            <EncarteNameBox
              value={encarteName ? encarteName : ""}
              onInput={(e) => {
                onEncarteNameChange(e.target.value);
              }}
              placeholder="O nome do seu encarte"
            />

            <ExportButton
              onDownload={async () => {
                setIsEncarteDownloading(true);
                await new Promise((resolve) => setTimeout(resolve, 100));
                await saveEncarte();
                await downloadCanvas();

                setIsEncarteDownloading(false);
              }}
              onShare={async () => {
                if (!isAccountPremium) return;
                setIsEncarteDownloading(true);
                await new Promise((resolve) => setTimeout(resolve, 100));
                await saveEncarte();
                await shareCanvas();
                setIsEncarteDownloading(false);
              }}
              isEncarteDownloading={isEncarteDownloading}
            />
            <div className="share-download-button-wrapper flex min-gap">
              <div className="position-relative">
                {(!isAccountPremium || accountStatus?.onTrial) && (
                  <PremiumTag />
                )}
                <Button
                  text="imprimir"
                  $NoBgColor
                  action={async () => {
                    if (!isAccountPremium) return;
                    setIsEncarteDownloading(true);
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    await saveEncarte();
                    await printCanvas();
                    setIsEncarteDownloading(false);
                  }}
                  icon={<IonIcon icon={printSharp} />}
                />
              </div>
              <div
                className="position-relative"
                style={{ marginRight: "2rem" }}
              >
                {(!isAccountPremium || accountStatus?.onTrial) && (
                  <PremiumTag />
                )}
                <Button
                  text="compartilhar"
                  $NoBgColor
                  action={async () => {
                    if (!isAccountPremium) return;
                    setIsEncarteDownloading(true);
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    await saveEncarte();
                    await shareCanvas();
                    setIsEncarteDownloading(false);
                  }}
                  icon={<IonIcon icon={shareSocialSharp} />}
                />
              </div>

              <Button
                text="baixar"
                action={async () => {
                  setIsEncarteDownloading(true);
                  await new Promise((resolve) => setTimeout(resolve, 100));
                  await saveEncarte();
                  await downloadCanvas();
                  setIsEncarteDownloading(false);
                }}
                icon={<IonIcon icon={downloadSharp} />}
              />
            </div>
          </div>
        </Header>
        {user != undefined && encarte !== undefined ? (
          <Layout>
            <Toolbar></Toolbar>

            <CanvasViewer></CanvasViewer>
          </Layout>
        ) : (
          <Loading txt={"carregando encarte..."} />
        )}
        {isEncarteDownloading ? (
          <Loading txt={"Baixando seu encarte..."} />
        ) : (
          ""
        )}
        <Help makeItSmallerOnMobile={true} />
      </Container>
    </WorkspaceContext.Provider>
  );
}

const extractProdInfo = (products) => {
  const map = products.map((p) => {
    return {
      id: p.id,
      name: p.name,
      promotionPrice: p.promotionPrice,
      imageURL: p.imageURL,
      measurement: p.measurement,
      styles: p.styles,
      takeX: p.takeX,
      payY: p.payY,
      normalSellingPrice: p.normalSellingPrice,
      promotionType: p.promotionType,
      varejoPrice: p.varejoPrice,
      atacadoPrice: p.atacadoPrice,
      quantity: p.quantity,
      obs: p.obs,
    };
  });
  return map;
};

const ExportButton = ({ onDownload, onShare, isEncarteDownloading }) => {
  const [showOptions, setShowOptions] = useState(false);
  const { isAccountPremium, accountStatus } = useOutletContext();

  return (
    <ExportButtonWrapper>
      <Button
        text="Exportar"
        action={() => setShowOptions((v) => !v)}
        icon={<IonIcon icon={downloadSharp} />}
        style={{ marginLeft: "1rem" }}
      />
      {showOptions && (
        <ExportOtpions style={{}}>
          <Button
            text="Para galeria"
            $NoBgColor
            action={onDownload}
            icon={<IonIcon icon={downloadSharp} />}
            disabled={isEncarteDownloading}
            style={{ width: "100%", marginBottom: "0.5rem" }}
          />
          <div className="tag-share-button-wrapper">
            {!isAccountPremium || (accountStatus?.onTrial && <PremiumTag />)}

            <Button
              text="Compartilhar"
              $NoBgColor
              action={onShare}
              icon={<IonIcon icon={shareSocialSharp} />}
              disabled={isEncarteDownloading}
              style={{ width: "100%" }}
            />
          </div>
        </ExportOtpions>
      )}
    </ExportButtonWrapper>
  );
};

async function progressivelyLoadProductsImgs(
  productsArr,
  token,
  setProducts,
  setIsDownloadingEncarteProductImages,
) {
  // Clone the array to avoid mutating the original
  let loadedProducts = [...productsArr];
  for (let i = 0; i < loadedProducts.length; i++) {
    // Convert image to DataURL
    loadedProducts[i] = await convertProductsImagesToDataURL(
      [loadedProducts[i]],
      token,
    ).then((res) => res[0]);
    // Convert price style to DataURL
    loadedProducts[i] = await convertPriceStyleToDataURL(
      [loadedProducts[i]],
      token,
    ).then((res) => res[0]);
    // Only update the products array, do not recalculate grid
    setProducts([...loadedProducts]);
  }
  setIsDownloadingEncarteProductImages(false);
}
